'use client';
import { ConfiguratorAccordion } from '@eliadmin/core/src/components/configurator/configurator-accordion';
import { ConfiguratorAddToCartDesktop } from '@eliadmin/core/src/components/configurator/configurator-add-to-cart-desktop';
import { getUnavailablePreviewLabel } from '@eliadmin/core/src/components/configurator/configurator-preview-copy';
import { ProformConfiguratorPreview } from '@eliadmin/core/src/components/configurator/proform-configurator-preview';
import { getProformPreviewModelForConfiguration } from '@eliadmin/core/src/components/configurator/proform-preview-assets';
import { useProformPreviewAvailability } from '@eliadmin/core/src/components/configurator/use-proform-preview-availability';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { Container } from '@eliadmin/core/src/components/ui/container';
import { Form } from '@eliadmin/core/src/components/ui/form';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import {
  useCleanConfiguratorMutation,
  useConfiguratorStore,
  useProformQuery,
} from '@eliadmin/core/src/stores/configurator-store';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { roundPrice } from '@eliadmin/core/src/utils/price-utils';
import { getProformPreviewUrl, parseProformNumber } from '@eliadmin/core/src/utils/proform-utils';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import Image from '@eliadmin/core/src/components/image/image-wrapped';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { RiBrush3Line } from 'react-icons/ri';
import { HtmlRendererRaw } from '@eliadmin/core/src/components/ui/html-renderer-raw';

export function ConfiguratorForm() {
  const form = useForm();

  const user_konf_id = useConfiguratorStore(s => s.user_konf_id);
  const conf = useProformQuery('GET_PRODUCT_CONF_ID', { user_konf_id });

  const { t } = useTranslations();
  const locale = useLocaleStore(s => s.currentLocale);

  const cleanMutation = useCleanConfiguratorMutation();

  const currency = useGlobalStore(s => s.generalLayout.currency);

  const config = useConfiguratorStore(s => ({
    productConf: s.productConf,
    productDetail: s.productDetail,
    productCols: s.productCols,
  }));

  const productDetail = useConfiguratorStore(s => s.productDetail);
  const productCode = useConfiguratorStore(s => s.cislo_nomenklatury);
  const imgPreviewUrl = getProformPreviewUrl(conf.data, productDetail);
  const previewModel = getProformPreviewModelForConfiguration(
    productCode,
    conf.data,
    config.productConf,
  );
  const previewAvailability = useProformPreviewAvailability(previewModel);
  const hasInteractivePreview = previewAvailability === 'available';
  const hasModelPreviewConvention = Boolean(previewModel);
  const [isDesktop, setIsDesktop] = useState(false);

  useEffect(() => {
    const media = window.matchMedia('(min-width: 768px)');
    const update = () => setIsDesktop(media.matches);
    update();
    media.addEventListener('change', update);
    return () => media.removeEventListener('change', update);
  }, []);

  useEffect(() => {
    // debug log
    if (process.env.NODE_ENV === 'development') {
      console.log({ ...config, productConfId: conf.data });
    }
  }, [conf.data, config]);

  return (
    <Form {...form}>
      <Container className="flex w-full max-w-[1600px] items-start justify-between gap-8">
        <div
          className={cn(
            'w-full min-w-[0px] md:min-w-[440px]',
            hasModelPreviewConvention ? 'max-w-[1104px]' : 'max-w-[1136px]',
          )}
        >
          <ConfiguratorAccordion />

          <div className="mt-4 flex w-full flex-col rounded-xl bg-muted p-5">
            <div className="flex w-full flex-row justify-between gap-2">
              <p className="text-sm font-normal text-muted-foreground">{t('text1449')}</p>
              {conf.data.root.smako_conf && (
                <p className="text-sm font-normal text-muted-foreground">
                  {/* TODO: toto nejako pocitat */}
                  {roundPrice(parseProformNumber(conf.data.root.smako_conf?.column.cena))}{' '}
                  {currency}
                </p>
              )}
            </div>
            <div className="flex w-full flex-row justify-between gap-2">
              <p className="text-lg font-semibold">
                {t('text1448')}
                <br></br>
              </p>
              {conf.data.root.smako_conf && (
                <p className="text-right text-lg font-semibold ">
                  {roundPrice(
                    parseProformNumber(conf.data.root.smako_conf?.column.cena) *
                      Math.max(parseProformNumber(conf.data.root.smako_conf.column.mnozstvi), 1),
                  )}{' '}
                  {currency}
                </p>
              )}
            </div>
          </div>

          <div className="mt-8 hidden items-center justify-end gap-4 text-base font-medium md:flex">
            <Button
              className="flex items-center justify-center gap-2.5 rounded-full border border-muted bg-background px-8 py-3 text-accent shadow-none hover:text-background"
              onClick={() => cleanMutation.mutate()}
            >
              <RiBrush3Line size={20} />
              {t('text1466')}
            </Button>

            <ConfiguratorAddToCartDesktop />
          </div>
        </div>

        <div
          className={cn(
            'sticky top-4 hidden w-full min-w-[220px] space-y-5 md:block',
            hasModelPreviewConvention ? 'max-w-[464px]' : 'max-w-[432px]',
          )}
        >
          <p className="text-sm font-medium text-muted-foreground">{t('text1452')}</p>

          {hasInteractivePreview && isDesktop ? (
            <ProformConfiguratorPreview
              className="min-h-[520px] lg:min-h-[630px]"
              productConfId={conf.data}
              productDetail={productDetail}
            />
          ) : hasModelPreviewConvention && previewAvailability === 'checking' ? (
            <div
              className="relative flex h-[400px] w-full items-center justify-center rounded-xl bg-muted px-6 text-center text-sm font-semibold text-muted-foreground lg:h-[630px]"
              role="status"
            >
              Ověřuji dostupnost náhledu…
            </div>
          ) : hasModelPreviewConvention && previewAvailability === 'unavailable' ? (
            <div
              className="relative flex h-[400px] w-full items-center justify-center rounded-xl border border-border bg-muted px-6 text-center lg:h-[630px]"
              role="status"
            >
              <p className="text-3xl font-semibold text-destructive">
                {getUnavailablePreviewLabel(locale)}
              </p>
            </div>
          ) : !hasInteractivePreview ? (
            <div
              className={cn(
                'relative flex h-[400px] w-full items-center justify-center text-center lg:h-[630px]',
                !imgPreviewUrl && 'bg-[#FFEBEB]',
              )}
            >
              {imgPreviewUrl ? (
                <Image
                  src={imgPreviewUrl}
                  alt={t('text1452')}
                  width={0}
                  height={0}
                  sizes="100vw"
                  className="h-full w-full object-contain"
                />
              ) : (
                <HtmlRendererRaw
                  as="h4"
                  className="text-3xl font-semibold text-destructive"
                  html={t('text1452').replace(/ /g, '<br/>')}
                />
              )}
            </div>
          ) : null}
        </div>
      </Container>
    </Form>
  );
}
